import Layout from "../Layout/Layout.tsx";
import ImageGallery from "../../componenets/ImageGallery/ImageGallery.tsx";
import { FaTrailer } from "react-icons/fa6";
import { PiToilet, PiOven } from "react-icons/pi";
import { TbTruckDelivery, TbRating21Plus } from "react-icons/tb";
import {
  MdPets,
  MdOutlineBed,
  MdBatteryChargingFull,
  MdSunnySnowing,
} from "react-icons/md";
import YouTubeVideo from "../../componenets/Youtube/Youtube.tsx";
import { JsonLDData } from "react-safe-json-ld";
import { logViewItem } from "../../lib/analytics.ts";
import React from "react";
import FAQs from "../../componenets/FAQs/FAQs.tsx";
import { FAQItem } from "../../componenets/FAQs/FAQItem.tsx";

const CommodoreDetail = () => {
  React.useEffect(() => {
    logViewItem('Commodore');
  }, []);

  const FAQData: FAQItem[] = [
    {
      question: "What is the Commodore best suited for?",
      answer: "Weekend getaways, wine tours, kayaking, fishing, and camping trips within 350 miles of DFW."
    },
    {
      question: "How many people can Commodore sleep?",
      answer: "Commodore comfortably sleeps 1-2 people on the pull out Twin XL bed."
    },
    {
      question: "Can I take Commodore off-grid?",
      answer: "Absolutely! Commodore has onboard batteries, solar panels, and carries 7 gallons of fresh water."
    },
    {
      question: "Is Commodore good for kayaking and fishing trips?",
      answer: "Yes, Commodore has roof storage for kayacks and poles and can tow a fishing boat or other watersports equipment"
    },
    {
      question: "Does Commodore having towing capability?",
      answer: "Yes, the frame mounted receiver is capable of towing up to 10,000 lbs."
    },
    {
      question: "Where can I pick up Commodore?",
      answer: "We are based in Richardson, TX."
    }
  ];

  const structuredData: JsonLDData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CamperVan",
        "name": "Commodore Landship Camper Van",
        "description": "Serenity is a rugged off-grid camper van on a Ford E450 chassis. Calm and zen interior sleeps 3-4 comfortably, with over a week of boondocking capability. Perfect for peaceful hiking, fishing, and remote work getaways within 350 miles of DFW. Can tow a boat or trailer.",
        "brand": { "@type": "Brand", "name": "Earthbound Martian" },
        "image": ["https://earthboundmartian.com/images/commodore1.jpg", "..."],
        "offers": { "@type": "Offer", "priceCurrency": "USD", "price": "110" },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "12"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": FAQData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  return (
    <Layout structuredData={structuredData}>
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Image + Title */}
        <div className="relative rounded-3xl overflow-hidden mb-12">
          <img
            src="/images/fleet/commodore/Cedar_Hill_Angl1-2.jpg"
            alt="Commodore"
            className="w-full h-[500px] object-cover object-bottom"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-10 left-10 text-white">
            <h1 className="text-6xl font-bold">Commodore</h1>
            <p className="text-2xl mt-2">Stealth Luxury on Wheels</p>
          </div>
        </div>

        {/* Quick Icons */}
        <div className="flex flex-wrap gap-6 mb-12">
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <TbTruckDelivery size={24} />
            </span>
            <div>
              <div className="font-semibold">Delivery Available</div>
              <div className="text-md text-zinc-500">Greater DFW Area</div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <MdPets size={24} />
            </span>
            <div className="font-semibold">Pet Friendly</div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <TbRating21Plus size={24} />
            </span>
            <div className="font-semibold">21+</div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <MdOutlineBed size={24} />
            </span>
            <div>
              <div className="font-semibold">Twin-XL</div>
              <div className="text-md text-zinc-500">Sleeps 2 Cozy</div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <FaTrailer size={24} />
            </span>
            <div>
              <div className="font-semibold">Towing</div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Column */}
          <div>
            <p className="text-xl text-zinc-600 leading-relaxed mb-10">
              Discover the freedom of quiet, sophisticated adventure in our 2006
              Ford E350 masterpiece. With its commanding 140" wheelbase and
              extended cargo, this elegant van glides like an extended
              Transit—yet feels like a private sanctuary on wheels.
              <br />
              <br />
              Though 54" tall inside, every seated moment on the plush couch or
              toilet offers generous headroom. Deep 5% tint and luxurious
              thermal curtains wrap you in total privacy, perfect for serene
              boondocking under the stars.
              <br />
              <br />
              360° security cameras and brilliant door spotlights stand guard,
              while the Twin XL pull-out bed invites two adults to rest in
              comfort.
              <br />
              <br />
              Elevate your road trip to pure luxury. Commodore awaits.
            </p>

            <div className="mb-10">
              <YouTubeVideo videoId="fGTjFQumbPY" />
            </div>

            {/* Vehicle Info */}
            <div className="mb-12">
              <h3 className="font-semibold text-xl mb-6">Vehicle Info</h3>
              <div className="grid grid-cols-2 gap-x-12 gap-y-4 text-sm">
                <div>
                  <span className="text-lg text-zinc-500">Length:</span> 20 ft
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Transmission:</span>{" "}
                  Automatic
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Fuel Type:</span> Gas
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Range:</span> ~ 350
                  miles
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Weight:</span> 10,000
                  lbs
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Sleeps:</span> 1-2
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="mb-12">
              <h3 className="font-semibold text-xl mb-8">Amenities</h3>

              <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
                {/* Bathroom */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <PiToilet size={24} />
                    </span>{" "}
                    Bathroom
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Cassette Toilet</li>
                    <li>• Toilet Paper</li>
                  </ul>
                </div>

                {/* Kitchen */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <PiOven size={24} />
                    </span>{" "}
                    Kitchen
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Propane stove with 2 burners</li>
                    <li>• Sink with running water</li>
                    <li>• Compact fridge</li>
                    <li>• 10 Gallon Fresh Water Tank</li>
                  </ul>
                </div>

                {/* Power & Hookups */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <MdBatteryChargingFull />
                    </span>{" "}
                    Power & Hookups
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Solar + battery system</li>
                    <li>• Shore power hookup</li>
                    <li>• Inverter</li>
                  </ul>
                </div>

                {/* Climate */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <MdSunnySnowing />{" "}
                    </span>{" "}
                    Climate & Comfort
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Portable AC</li>
                    <li>• Thinsulate insulation</li>
                    <li>• Thermal curtains</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Booking */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm">
              <h3 className="font-semibold text-2xl mb-6">
                Ready to book Commodore?
              </h3>

              <a
                href="https://www.outdoorsy.com/rv-rental/richardson_tx/2006_ford_e350-box-van_471182-listing"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-mars hover:bg-sunset text-white text-center py-6 rounded-2xl font-semibold text-lg transition-all mb-4"
              >
                Book on Outdoorsy
              </a>

              <p className="text-center text-sm text-zinc-500">
                Instant booking • Protected by Outdoorsy insurance
              </p>
            </div>
          </div>
        </div>

        {/* Full Gallery Section */}
        <div className="mt-24">
          <h3 className="text-2xl font-semibold mb-8">Full Tour</h3>
          <ImageGallery
            images={[
              "/images/fleet/commodore/Cedar_Hill_Angl1-2.jpg",
              "/images/fleet/commodore/OK_Camping_2025.jpg",
              "/images/fleet/commodore/interior_4.jpg",
              "/images/fleet/commodore/interior_1.jpg",
              "/images/fleet/commodore/interior_2.jpg",
              "/images/fleet/commodore/interior_3.jpg",
            ]}
          />
        </div>

        <section className="flex mt-10 p-5 bg-zinc-100 rounded-xl">
          <FAQs faqs={FAQData} />
        </section>

      </div>
    </Layout>
  );
};

export default CommodoreDetail;
